import { Inject, Injectable } from '@angular/core';
import { JwtDecoderService } from '../decoder-service/jwt-decoder.service';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth, { AuthState } from '@okta/okta-auth-js';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { WebServices } from '../web-service/web.services';
import { UserModel } from 'src/app/models/user.model';
import { ApigeeAuthService } from 'src/app/services/apigee-service/apigee-service';

@Injectable({
  providedIn: 'root'
})
export class AuthServiceService {
  public userGroups: string[] = [];
  public user: UserModel = {
    firstName: '',
    lastName: '',
    email: '',
    repackager: 'default'
  };
  private _username = new BehaviorSubject<string>('');
  username$ = this._username.asObservable();

  private repackagerNameSubject = new BehaviorSubject<string | null>(null);
  repackagerName$ = this.repackagerNameSubject.asObservable();

  email: any;
  externalEmail = '';
  externalUserSgln = '';

  constructor(
    public jwtDecoder: JwtDecoderService,
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    public router: Router,
    private webservice: WebServices,
    private apigeeAuthService: ApigeeAuthService,
  ) {
    this.authenticateUser();
  }

  // Subscribe to Okta auth state and load user info when authenticated
  public authenticateUser(): void {
    this.oktaAuth.authStateManager.subscribe((authState: AuthState) => {
      if (authState.isAuthenticated) {
        this.loadUserGroups();
      }
    });
  }

  public loadUserGroups(): void {
    const tokenStorage = localStorage.getItem('okta-token-storage');
    if (!tokenStorage) {
      return;
    }
    try {
      const parsedToken = JSON.parse(tokenStorage);
      const idToken = parsedToken.idToken?.idToken;

      if (!idToken) {
        return;
      }

      const decodedToken = this.jwtDecoder.decode(idToken);
      this.userGroups = decodedToken.groups || [];

      // Primary source: name from idToken payload
      let resolvedUsername: string = decodedToken.name || '';

      // Fallback: decode the Okta access token (JWT) - used for API calls
      if (!resolvedUsername) {
        resolvedUsername = this.extractUsernameFromOktaToken();
      }

      this._username.next(resolvedUsername);
      this.email = localStorage.getItem('usersEmail');

      this.updateUserInfo();
      if (this.email) {
        const isExternal = this.isExternalUser(this.email);
        if (isExternal) {
          this.externalEmail = this.email;
          localStorage.setItem('userType', 'external');
        }
      }
    } catch (error) {
    }
  }

  // Decodes the Okta access token (JWT used for all API calls) to extract username
  public extractUsernameFromOktaToken(): string {
    const accessToken = this.oktaAuth.getAccessToken();
    if (!accessToken) {
      return '';
    }
    return this.jwtDecoder.extractUsernameFromToken(accessToken);
  }

  // Synchronously returns the current username value
  public getUsername(): string {
    return this._username.getValue();
  }
  public isExternalUser(email: string): boolean {
    return !email.toLowerCase().endsWith('@cardinalhealth.com');
  }

  getGroups(): string[] {
    return this.userGroups;
  }

  isInGroup(group: string): boolean {
    return this.userGroups.includes(group);
  }

  getAccessLevel(): 'admin' | 'inboundAdmin' | 'outboundAdmin' | 'inboundUser' | 'outboundUser' | 'restricted' {
    const groups = this.userGroups;

    if (groups.includes('a-OktaSSO-APM0031544-EVAdmin-admins')) return 'admin';
    if (groups.includes('a-OktaSSO-APM0031544-EVIBAdmin-admins')) return 'inboundAdmin';
    if (groups.includes('a-OktaSSO-APM0031544-EVOBAdmin-admins')) return 'outboundAdmin';
    if (groups.includes('a-OktaSSO-APM0031544-EVIB-users')) return 'inboundUser';
    if (groups.includes('a-OktaSSO-APM0031544-EVOB-users')) return 'outboundUser';

    return 'restricted';
  }

  getApigeeToken(): Observable<any> {
    // Delegate token acquisition to the centralized ApigeeAuthService
    return this.apigeeAuthService.getAccessToken();
  }

  public updateUserInfo(): void {
    // Removed server-side user API call — rely on token claims and local storage for user info.
    const tokenName = this.extractUsernameFromOktaToken();
    if (tokenName) {
      this._username.next(tokenName);
    }
  }
  logout(): void {

    this.oktaAuth.signOut({
      postLogoutRedirectUri: environment.postLogoutRedirectUri
    }).then(() => {
      localStorage.removeItem('apigee_token')
      localStorage.removeItem('okta-cache-storage');
      localStorage.removeItem('okta-original-uri-storage');
      localStorage.removeItem('okta-shared-transaction-storage');
      localStorage.removeItem('okta-shared-transaction-storage');
      localStorage.removeItem('okta-token-storage');
      localStorage.removeItem('userType');
      localStorage.removeItem('usersEmail');
      //this.router.navigateByUrl('/dashboard').then();
    }).catch(() => {
    });
  }
}
